import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Eye, ChevronRight, ArrowRight } from 'lucide-react';
import { fetchHeroSettings } from '@/lib/api';

const DEFAULT_VIDEO = 'https://videos.pexels.com/video-files/3191572/3191572-uhd_2560_1440_25fps.mp4';
const DEFAULT_POSTER = 'https://images.unsplash.com/photo-1763058673652-e13a543bc461?w=1920&h=1080&fit=crop&q=80';
const MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3236.5!2d-78.8503!3d35.7327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac8f4b88e0e1a7%3A0x0!2s810+Center+St%2C+Apex%2C+NC+27502!5e0!3m2!1sen!2sus!4v1700000000000";

const SERVICES = [
  { id: 'tunnels', title: 'Batting Tunnels', desc: '6 indoor tunnels for cricket, baseball, and softball. Hourly rentals, team blocks, and pitching machines.', cta: 'Book Now', link: '/batting-tunnels', external: true, href: 'https://propelsports.myesoftplanner.com' },
  { id: 'parties', title: 'Parties & Events', desc: 'Birthday parties, fundraisers, corporate events, and special occasions. Custom packages available.', cta: 'Learn More', link: '/parties' },
  { id: 'tournaments', title: 'Tournaments & Events', desc: 'Host your pickleball, box cricket, or futsal tournament at our facility. Portable courts available.', cta: 'View Events', link: '/tournaments' },
];

const DEFAULT_HEADING = "Apex's Multi-Sport Indoor Home";
const DEFAULT_SUBTITLE = "Parties & events · Batting tunnels · Tournaments · Facility rentals";
const DEFAULT_CTA_PRIMARY = "Book Now";
const DEFAULT_CTA_PRIMARY_LINK = "https://propelsports.myesoftplanner.com";
const DEFAULT_CTA_SECONDARY = "View Facility";
const DEFAULT_CTA_SECONDARY_LINK = "/facility";

export function HeroSection() {
  const videoRef = useRef(null);
  const [heroVideo, setHeroVideo] = useState(DEFAULT_VIDEO);
  const [heroPoster, setHeroPoster] = useState(DEFAULT_POSTER);
  const [heading, setHeading] = useState(DEFAULT_HEADING);
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);
  const [ctaPrimaryText, setCtaPrimaryText] = useState(DEFAULT_CTA_PRIMARY);
  const [ctaPrimaryLink, setCtaPrimaryLink] = useState(DEFAULT_CTA_PRIMARY_LINK);
  const [ctaSecondaryText, setCtaSecondaryText] = useState(DEFAULT_CTA_SECONDARY);
  const [ctaSecondaryLink, setCtaSecondaryLink] = useState(DEFAULT_CTA_SECONDARY_LINK);

  useEffect(() => {
    fetchHeroSettings().then(data => {
      if (data.video_url) setHeroVideo(data.video_url);
      if (data.poster_url) setHeroPoster(data.poster_url);
      if (data.heading) setHeading(data.heading);
      if (data.subtitle) setSubtitle(data.subtitle);
      if (data.cta_primary_text) setCtaPrimaryText(data.cta_primary_text);
      if (data.cta_primary_link) setCtaPrimaryLink(data.cta_primary_link);
      if (data.cta_secondary_text) setCtaSecondaryText(data.cta_secondary_text);
      if (data.cta_secondary_link) setCtaSecondaryLink(data.cta_secondary_link);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [heroVideo]);

  const isExternal = ctaPrimaryLink.startsWith('http');
  const isSecondaryExternal = ctaSecondaryLink.startsWith('http');

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <video ref={videoRef} key={heroVideo} autoPlay muted loop playsInline poster={heroPoster} className="w-full h-full object-cover" preload="auto">
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#0B1D3A]/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1D3A]/40 via-transparent to-[#0B1D3A]/60" />
      </div>
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl md:text-[64px] font-bold text-white mb-5 leading-[1.1] tracking-tight animate-slide-up whitespace-pre-line">
          {heading}
        </h1>
        <p className="text-white/75 text-base sm:text-lg mb-10 max-w-2xl mx-auto animate-slide-up-delay font-light whitespace-pre-line">
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up-delay-2">
          {isExternal ? (
            <a href={ctaPrimaryLink} target="_blank" rel="noopener noreferrer" data-testid="hero-book-now"
              className="flex items-center gap-2.5 bg-[#F26522] hover:bg-[#D9551A] text-white px-10 py-4 rounded-md font-semibold text-[15px] transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/20">
              <Calendar size={18} /> {ctaPrimaryText}
            </a>
          ) : (
            <Link to={ctaPrimaryLink} data-testid="hero-book-now"
              className="flex items-center gap-2.5 bg-[#F26522] hover:bg-[#D9551A] text-white px-10 py-4 rounded-md font-semibold text-[15px] transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/20">
              <Calendar size={18} /> {ctaPrimaryText}
            </Link>
          )}
          {isSecondaryExternal ? (
            <a href={ctaSecondaryLink} target="_blank" rel="noopener noreferrer" data-testid="hero-view-facility"
              className="flex items-center gap-2.5 border border-white/60 hover:border-white text-white px-10 py-4 rounded-md font-semibold text-[15px] transition-all duration-300 hover:bg-white/10">
              <Eye size={18} /> {ctaSecondaryText}
            </a>
          ) : (
            <Link to={ctaSecondaryLink} data-testid="hero-view-facility"
              className="flex items-center gap-2.5 border border-white/60 hover:border-white text-white px-10 py-4 rounded-md font-semibold text-[15px] transition-all duration-300 hover:bg-white/10">
              <Eye size={18} /> {ctaSecondaryText}
            </Link>
          )}
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex justify-center pt-1.5">
          <div className="w-1 h-2 bg-[#F26522] rounded-full" />
        </div>
      </div>
    </section>
  );
}

export function WhatWeDoSection() {
  return (
    <section data-testid="what-we-do-section" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1D3A] mb-3">What We Do</h2>
          <p className="text-gray-500 text-lg">We provide the space — you bring the fun</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map(svc => (
            <div key={svc.id} data-testid={`service-${svc.id}`} className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-[#F26522]/30 hover:shadow-xl transition-all duration-300 flex flex-col">
              <h3 className="text-xl font-bold text-[#0B1D3A] mb-3">{svc.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1">{svc.desc}</p>
              {svc.external ? (
                <a href={svc.href} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#F26522] font-semibold text-sm hover:gap-3 transition-all">
                  {svc.cta} <ArrowRight size={16} />
                </a>
              ) : (
                <Link to={svc.link} className="inline-flex items-center gap-2 text-[#F26522] font-semibold text-sm hover:gap-3 transition-all">
                  {svc.cta} <ArrowRight size={16} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function UpcomingEventsSection({ events }) {
  if (!events.length) return null;
  return (
    <section data-testid="upcoming-events-section" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-3xl font-bold text-[#0B1D3A] mb-1">Upcoming Events</h2>
            <p className="text-gray-500">Tournaments and leagues at our facility</p>
          </div>
          <Link to="/tournaments" className="flex items-center gap-1 text-[#F26522] text-sm font-semibold hover:gap-2 transition-all">
            View All <ChevronRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {events.map(ev => (
            <div key={ev.id} data-testid={`home-event-${ev.id}`} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-[#F26522]/30 hover:shadow-lg transition-all">
              <div className="text-[#F26522] text-xs font-semibold mb-1">{ev.sport}</div>
              <h3 className="text-lg font-bold text-[#0B1D3A] mb-2">{ev.name}</h3>
              <div className="bg-[#F26522]/10 text-[#F26522] text-xs font-semibold px-3 py-1 rounded-full inline-block mb-3">
                {ev.status === 'coming_soon' ? 'Coming Soon' : ev.status}
              </div>
              <p className="text-gray-500 text-sm">{ev.format}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FacilityStatsSection({ stats }) {
  if (!stats) return null;
  return (
    <section className="py-14 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {Object.entries(stats).map(([key, val]) => (
            <div key={key} className="text-center p-5 rounded-xl bg-white border border-gray-100 hover:border-[#F26522]/30 hover:shadow-md transition-all duration-300">
              <div className="text-2xl md:text-3xl font-bold text-[#F26522] mb-0.5">{val}</div>
              <div className="text-gray-500 text-sm capitalize">{key}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function VisitUsSection({ hours }) {
  return (
    <section data-testid="visit-us-section" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-[#0B1D3A] mb-10 text-center">Visit Us</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="rounded-2xl overflow-hidden shadow-md border border-gray-200 mb-4">
              <iframe src={MAPS_EMBED} width="100%" height="300" style={{ border: 0 }} allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade" title="Propel Sports NC" />
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <span className="font-medium text-[#0B1D3A]">810 Center St, Building #4, Apex NC, 27502</span>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#0B1D3A] mb-4">Hours</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-2.5">
              {Object.entries(hours).map(([day, time]) => (
                <div key={day} className="flex justify-between items-center py-0.5">
                  <span className="text-[#0B1D3A] font-medium text-sm capitalize">{day}</span>
                  <span className="text-gray-500 text-sm">{time}</span>
                </div>
              ))}
              {Object.keys(hours).length === 0 && (
                <p className="text-gray-400 text-sm text-center py-4">Hours loading...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CtaSection() {
  return (
    <section className="py-20 bg-[#0B1D3A]">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Book?</h2>
        <p className="text-gray-400 mb-8">Reserve your party, tunnel, or event space today!</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/parties" data-testid="cta-book-party"
            className="flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-8 py-4 rounded-md font-semibold transition-all hover:shadow-xl hover:shadow-orange-500/20">
            Book a Party
          </Link>
          <Link to="/contact"
            className="flex items-center gap-2 border border-white/30 hover:border-white text-white px-8 py-4 rounded-md font-semibold transition-all hover:bg-white/5">
            Contact Us
          </Link>
        </div>
      </div>
    </section>
  );
}
